/**
 * Iter 428.p6 — Reports & Technical row for CustomerProfileDocuments-
 * Block and ProjectDocumentsCard.
 *
 * Reads from the new `customer_documents` collection (Engineering-Desk
 * "Create report" pipeline). Renders as another folder inside the
 * existing Documents tab, MATCHING the visual language of the block's
 * SectionRow (bordered rounded-md card, chevron + icon + count pill,
 * collapsible body). NOT `hv-card` — the surrounding block already
 * wraps sections in its own card.
 *
 * Scoped by exactly ONE of:
 *   • customerId — surfaces all reports for a customer
 *   • projectId  — surfaces all reports whose family.project_id ==
 *                  this project. Same rows show up on both surfaces
 *                  (the "one doc, two homes" pattern Dirk asked for).
 *
 * No upload button — reports are LLM-generated on the Engineering
 * Desk, not manually uploaded here.
 */
import { useEffect, useState } from "react";
import {
  ChevronDown, ChevronRight, Sparkles, Loader2, ExternalLink,
  Trash2, FileText,
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const ACCENT = "#0d6e75";

const KIND_LABEL = {
  pump_assessment:   "Pump Assessment",
  technical_report:  "Technical Report",
  engineering_note:  "Engineering Note",
  inspection:        "Inspection Report",
  other:             "Report",
};

function chainFor(doc) {
  const fam = doc?.family || {};
  const parts = [];
  if (fam.project_name) parts.push(fam.project_name);
  const so = (fam.sales_order_refs || [])[0]?.ref;
  const st = (fam.ticket_refs      || [])[0]?.ref;
  const jc = (fam.job_card_refs    || [])[0]?.ref;
  if (so) parts.push(so);
  if (st) parts.push(st);
  if (jc) parts.push(jc);
  return parts.join(" · ");
}

export default function ReportsTechnicalSection({
  customerId, projectId, emptyHint,
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openingId, setOpeningId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!customerId && !projectId) return;
      setLoading(true);
      try {
        const params = {};
        if (customerId) params.customer_id = customerId;
        if (projectId)  params.project_id  = projectId;
        const { data } = await api.get("/customer-documents", { params });
        if (!cancelled) setItems(data?.items || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [customerId, projectId]);

  // Auto-open the folder when there's content, so the user finds
  // reports without hunting through collapsed sections.
  useEffect(() => {
    if (items.length > 0) setOpen(true);
  }, [items.length]);

  const openPdf = async (doc) => {
    setOpeningId(doc.id);
    try {
      const r = await api.get(`/customer-documents/${doc.id}/pdf`, {
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't open the PDF.");
    } finally {
      setOpeningId(null);
    }
  };

  const del = async (doc) => {
    if (!window.confirm(`Delete "${doc.title}"?`)) return;
    setDeletingId(doc.id);
    try {
      await api.delete(`/customer-documents/${doc.id}`);
      setItems((prev) => prev.filter((d) => d.id !== doc.id));
      toast.success("Document deleted.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete document.");
    } finally {
      setDeletingId(null);
    }
  };

  const count = items.length;

  return (
    <div
      className="border border-white/10 rounded-md"
      data-testid="pd-section-reports_technical"
    >
      <div className="flex items-center gap-2 p-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 min-w-0 flex-1 text-left hover:opacity-80"
          data-testid="pd-section-reports_technical-toggle"
        >
          {open
            ? <ChevronDown className="w-4 h-4 text-[#647087] shrink-0" />
            : <ChevronRight className="w-4 h-4 text-[#647087] shrink-0" />}
          <div
            className="w-6 h-6 rounded-md flex items-center justify-center shrink-0"
            style={{ background: `${ACCENT}22`, border: `1px solid ${ACCENT}55` }}
          >
            <Sparkles className="w-3.5 h-3.5" style={{ color: ACCENT }} />
          </div>
          <div className="font-medium text-sm truncate">Reports &amp; Technical</div>
          <span
            className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border"
            style={{ borderColor: `${ACCENT}40`, color: ACCENT }}
          >
            {count}
          </span>
          <span className="text-[10px] text-[#647087] hidden sm:inline">
            Engineering &amp; workbench
          </span>
        </button>
        {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#647087]" />}
      </div>

      {open && (
        <div className="border-t border-white/10 p-3">
          {count === 0 ? (
            <div className="text-xs text-[#647087] italic py-4 text-center">
              {emptyHint || "No reports yet — generate one from the Engineering Desk."}
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {items.map((doc) => {
                const chain = chainFor(doc);
                return (
                  <div
                    key={doc.id}
                    className="py-2 flex items-center gap-2"
                    data-testid={`reports-technical-row-${doc.id}`}
                  >
                    <FileText className="w-4 h-4 text-[#1FB6C1] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] text-[#cbd2df] truncate">
                        {doc.title}
                      </div>
                      <div className="text-[10px] text-[#647087] mt-0.5 truncate">
                        {KIND_LABEL[doc.kind] || doc.kind}
                        {doc.generated_by_name ? ` · ${doc.generated_by_name}` : ""}
                        {doc.generated_at ? ` · ${new Date(doc.generated_at).toLocaleDateString()}` : ""}
                        {chain ? ` · ${chain}` : ""}
                      </div>
                    </div>
                    <button
                      onClick={() => openPdf(doc)}
                      disabled={openingId === doc.id}
                      className="hv-btn hv-btn-ghost text-xs inline-flex items-center gap-1"
                      data-testid={`reports-technical-open-${doc.id}`}
                      title="Open PDF in a new tab"
                    >
                      {openingId === doc.id
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <ExternalLink className="w-3.5 h-3.5" />}
                      <span className="hidden sm:inline">Open</span>
                    </button>
                    <button
                      onClick={() => del(doc)}
                      disabled={deletingId === doc.id}
                      className="hv-btn hv-btn-ghost text-xs text-red-300 hover:!text-red-200"
                      data-testid={`reports-technical-delete-${doc.id}`}
                      title="Delete"
                    >
                      {deletingId === doc.id
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <Trash2 className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
